import { FC } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Box, Typography, Collapse } from '@mui/material';

import { useExpansion } from '../../../../../hooks/Expansion';
import ApiService from '../../../../../ApiService';
import { CardContent, CardHeader } from './Card';
import CollapsibleCard, { CollapsibleCardProps } from '../../../../../components/Card/CollapsibleCard/CollapsibleCard';
import CollapsibleCardExpandArea from '../../../../../components/Card/CollapsibleCard/CollapsibleCardExpandArea';
import CollapsibleCardExpandButton from '../../../../../components/Card/CollapsibleCard/CollapsibleCardExpandButton';
import Column from '../../../../../components/Column';
import ExpandButton from '../../../../../components/Button/ExpandButton';
import Row from '../../../../../components/Row';

import { CampaignAdGroup } from '../../../../../types/Campaign';
import { DspGeoLocation } from '../../../../../types/DspGeoLocation';
import CampaignAdGroupLocationUtils from '../../../../../utils/CampaignAdGroupLocationUtils';
import CampaignAdGroupGeoLocationTargetingDataGrid from '../DataGrid/CampaignAdGroupGeoLocationTargetingDataGrid';
import CampaignAdGroupGeoLocationUtils from '../../../../../utils/CampaignAdGroupGeoLocationUtils';
import CampaignAdGroupLocationTargetingChips from '../Chip/CampaignAdGroupLocationTargetingChips';
import CampaignAdGroupLocationTargetingSummaryText from '../Text/CampaignAdGroupLocationTargetingSummaryText';

interface CampaignAdGroupLocationTargetingCardProps {
    campaignAdGroup: CampaignAdGroup;
    slotProps?: {
        collapsibleCard?: Partial<CollapsibleCardProps>;
    };
}

const CampaignAdGroupLocationTargetingCard: FC<CampaignAdGroupLocationTargetingCardProps> = ({
    campaignAdGroup,
    slotProps = {},
}) => {
    const { expanded, toggleExpanded } = useExpansion();

    const dspLocationTargetIds: string[] = CampaignAdGroupLocationUtils.toDspGeoLocationIds(
        campaignAdGroup?.adGroupLocations || []
    );
    const dspLocationQuery = useQuery<DspGeoLocation[]>({
        enabled: !!dspLocationTargetIds.length,
        refetchOnWindowFocus: false,
        queryKey: ['campaign/adGroup/dspLocations', campaignAdGroup.id, dspLocationTargetIds],
        queryFn: async () => {
            const { data } = await ApiService.getDspGeoLocations({
                ids: dspLocationTargetIds,
            });

            return data;
        },
    });

    const dspGeoLocationTargetIds: string[] = CampaignAdGroupGeoLocationUtils.toDspGeoLocationIds(
        campaignAdGroup?.adGroupGeoLocations || []
    );
    const dspGeoLocationQuery = useQuery<DspGeoLocation[]>({
        enabled: !!dspGeoLocationTargetIds.length,
        refetchOnWindowFocus: false,
        queryKey: ['campaign/adGroup/dspGeoLocations', campaignAdGroup.id, dspGeoLocationTargetIds],
        queryFn: async () => {
            const { data } = await ApiService.getDspGeoLocations({
                ids: dspGeoLocationTargetIds,
            });

            return data;
        },
    });

    return (
        <CollapsibleCard defaultExpanded {...slotProps?.collapsibleCard}>
            <CardHeader
                title={
                    <Row alignItems="center" gap={1}>
                        <Typography variant="body1">Location</Typography>
                    </Row>
                }
                action={<CollapsibleCardExpandButton />}
            />
            <CollapsibleCardExpandArea>
                <CardContent>
                    <Column gap={3}>
                        <Column gap={2}>
                            <CampaignAdGroupLocationTargetingSummaryText
                                dspLocations={dspLocationQuery.data || []}
                                dspGeoLocations={dspGeoLocationQuery.data || []}
                            />

                            <Row gap={1}>
                                <CampaignAdGroupLocationTargetingChips items={dspLocationQuery.data || []} />
                            </Row>
                        </Column>

                        <Column>
                            <Box>
                                <ExpandButton
                                    variant="outlined"
                                    size="large"
                                    expand={expanded}
                                    onClick={toggleExpanded}
                                >
                                    {expanded ? 'Hide All Selections' : 'See All Selections'}
                                </ExpandButton>
                            </Box>

                            <Collapse in={expanded} timeout="auto" unmountOnExit>
                                {dspGeoLocationQuery.isError ? (
                                    <Alert severity="error">Failed to load location targeting.</Alert>
                                ) : (
                                    <CampaignAdGroupGeoLocationTargetingDataGrid
                                        rows={dspGeoLocationQuery.data || []}
                                        loading={dspGeoLocationQuery.isLoading}
                                    />
                                )}
                            </Collapse>
                        </Column>
                    </Column>
                </CardContent>
            </CollapsibleCardExpandArea>
        </CollapsibleCard>
    );
};

export default CampaignAdGroupLocationTargetingCard;
